import {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import '../../../assets/styles/profile/referrals.scss';
import usePageMeta from '../../../services/page-meta';

function normalizePhone(value) {
    const digits = String(value || '').replace(/\D/g, '');
    if (!digits) {
        return '';
    }

    return `+${digits}`;
}

function copyToClipboard(value) {
    const text = String(value || '');
    if (!text) {
        return Promise.resolve(false);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    }

    const input = document.createElement('input');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(input);
    return Promise.resolve(Boolean(copied));
}

function formatCell(value) {
    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
}

function buildSearchableString(item) {
    return [
        item?.first_name,
        item?.last_name,
        item?.email,
        item?.phone,
        item?.registrationDate,
        item?.materialCount,
        item?.courseCount,
        item?.surveyCount,
    ].map((value) => String(value || '')).join(' ').toLowerCase();
}

export default function ProfileReferralsPage() {
    const {t} = useTranslation();
    usePageMeta({
        title: t('meta.profileReferrals.title', t('referral.header')),
        description: t('meta.profileReferrals.description', t('app.description')),
        keywords: t('meta.profileReferrals.keywords', t('app.keywords')),
    });

    const [isLoading, setIsLoading] = useState(true);
    const [payload, setPayload] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [search, setSearch] = useState('');
    const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
    const [phone, setPhone] = useState('');
    const [isSubmittingPhone, setIsSubmittingPhone] = useState(false);
    const [submitPhoneError, setSubmitPhoneError] = useState('');

    useEffect(() => {
        let isCancelled = false;

        fetch('/api/v1/profile/referrals', {credentials: 'same-origin'})
            .then(async (response) => {
                let data = {};
                try {
                    data = await response.json();
                } catch (e) {
                    data = {};
                }

                if (!response.ok) {
                    throw data;
                }

                return data;
            })
            .then((data) => {
                if (isCancelled) {
                    return;
                }

                if ((data?.state === 'redirect' || data?.state === 'unauthorized') && data?.redirectUrl) {
                    window.location.href = data.redirectUrl;
                    return;
                }

                setPayload(data);
                setPhone(data?.phone || '');
            })
            .catch((errorPayload) => {
                if (isCancelled) {
                    return;
                }

                const redirectUrl = errorPayload?.redirectUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                    return;
                }

                setHasError(true);
            })
            .finally(() => {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, []);

    const users = useMemo(() => payload?.users || [], [payload?.users]);

    const filteredUsers = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) {
            return users;
        }

        return users.filter((item) => buildSearchableString(item).includes(needle));
    }, [search, users]);

    const handleCopyLink = (event) => {
        event.preventDefault();
        copyToClipboard(payload?.referralLink || '');
    };

    const handleSendPhone = (event) => {
        event.preventDefault();

        if (isSubmittingPhone) {
            return;
        }

        const normalizedPhone = normalizePhone(phone);
        if (!normalizedPhone) {
            return;
        }

        setIsSubmittingPhone(true);
        setSubmitPhoneError('');

        const formData = new FormData();
        formData.append('phone', normalizedPhone);
        formData.append('referralLink', payload?.referralLink || '');

        fetch(payload?.actions?.sendSmsUrl || '/api/v1/account/referral/sms', {
            method: 'POST',
            credentials: 'same-origin',
            body: formData,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('send_sms_failed');
                }

                setPayload((prev) => ({...(prev || {}), phone: normalizedPhone}));
                setIsPhoneModalOpen(false);
            })
            .catch(() => {
                setSubmitPhoneError(t('referral.errorAddingToDatabase'));
            })
            .finally(() => {
                setIsSubmittingPhone(false);
            });
    };

    return (
        <section className="referrals-page">
            <div className="container">
                <div className="wrap">
                    {isLoading && <div className="referrals-placeholder" />}
                    {!isLoading && hasError && (
                        <div className="referrals-error">{t('referral.errorAddingToDatabase')}</div>
                    )}
                    {!isLoading && !hasError && payload && (
                        <>
                            <div className="generate-referral-link-block">
                                <h4>{t('referral.header')}</h4>
                                <div className="generate-referral-form">
                                    <form onSubmit={(event) => event.preventDefault()}>
                                        <input
                                            type="text"
                                            className="form-input"
                                            name="referralLink"
                                            value={payload?.referralLink || ''}
                                            readOnly
                                        />
                                        <div className="submit-block">
                                            <button className="btn btn-default" type="button" onClick={handleCopyLink}>
                                                {t('referral.copyLink')}
                                            </button>
                                            <button
                                                className="btn btn-primary"
                                                type="button"
                                                onClick={() => {
                                                    setSubmitPhoneError('');
                                                    setPhone(payload?.phone || '');
                                                    setIsPhoneModalOpen(true);
                                                }}>
                                                {t('referral.sendToMyNumber')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>

                            <div className="statistics-registered-users-block">
                                <h4>{t('referral.list.header')}</h4>
                                <h6>{t('referral.list.amount')} {users.length}</h6>
                                <div className="search-registered-users">
                                    <div className="flex-baseline">
                                        <div className="control-wrap">
                                            <label className={`form-label addon-label${search ? ' active' : ''}`} htmlFor="search">
                                                {t('referral.list.searchData')}
                                            </label>
                                            <i className="form-control-addon search"></i>
                                            <input
                                                type="text"
                                                className="form-input"
                                                id="search"
                                                value={search}
                                                onChange={(event) => setSearch(event.target.value)}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="registered-users-list">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>{t('referral.list.tableColumns.firstName')}</th>
                                                <th>{t('referral.list.tableColumns.lastName')}</th>
                                                <th>{t('referral.list.tableColumns.email')}</th>
                                                <th>{t('referral.list.tableColumns.phone')}</th>
                                                <th>{t('referral.list.tableColumns.registrationDate')}</th>
                                                <th>{t('referral.list.tableColumns.materials')}</th>
                                                <th>{t('referral.list.tableColumns.courses')}</th>
                                                <th>{t('referral.list.tableColumns.surveys')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map((item, index) => (
                                                <tr key={`${item?.email || 'user'}-${item?.phone || 'phone'}-${index}`}>
                                                    <td>{formatCell(item?.first_name)}</td>
                                                    <td>{formatCell(item?.last_name)}</td>
                                                    <td>{formatCell(item?.email)}</td>
                                                    <td>{formatCell(item?.phone)}</td>
                                                    <td>{formatCell(item?.registrationDate)}</td>
                                                    <td>{formatCell(item?.materialCount)}</td>
                                                    <td>{formatCell(item?.courseCount)}</td>
                                                    <td>{formatCell(item?.surveyCount)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {isPhoneModalOpen && (
                <div className="modal-overlay is-visible flex-center">
                    <div className="modal-content">
                        <div className="close-block">
                            <span className="close" onClick={() => setIsPhoneModalOpen(false)}></span>
                        </div>
                        <h6>{t('referral.enterPhoneToShareLink')}</h6>
                        <form onSubmit={handleSendPhone}>
                            <div className="form-group">
                                <label htmlFor="phone" className="form-label active">
                                    {t('referral.phoneNumber')}
                                </label>
                                <input
                                    id="phone"
                                    type="text"
                                    name="phone"
                                    className="form-input"
                                    value={phone}
                                    onChange={(event) => setPhone(event.target.value)}
                                />
                            </div>
                            <div className="submit-block">
                                <button className="btn btn-default" type="submit" disabled={!normalizePhone(phone) || isSubmittingPhone}>
                                    {t('referral.send')}
                                </button>
                            </div>
                            <div className="error-text">{submitPhoneError}</div>
                        </form>
                    </div>
                </div>
            )}
        </section>
    );
}
